import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AuthApi, UnauthApi } from '@/api';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { useAuthStore } from '@/stores/AuthStore';
import type { DeviceTokenData } from '@/stores/DeviceStore';
import { STATUS_COLOR, useDeviceInfoStore } from '@/stores/DeviceStore';

import { MESSAGE_KEYWORD } from '../_utils/reserved-keywords';
import { useHandleApp } from './useHandleApp';

export const SAFE_AREA_BOTTOM_IOS = 16;

export const useMountAppData = () => {
  const [
    osType,
    setAppVersion,
    tempDeviceTokenData,
    setTempDeviceTokenData,
    deviceTokenData,
    setDeviceTokenData,
  ] = useDeviceInfoStore(
    useShallow((state) => [
      state.osType,
      state.setAppVersion,
      state.tempDeviceTokenData,
      state.setTempDeviceTokenData,
      state.deviceTokenData,
      state.setDeviceTokenData,
    ]),
  );
  const { request: requestAppVersion, response: responseAppVersion } =
    useHandleApp<{
      version: string;
      isFirstLoading: 'Y' | 'N';
    }>({
      type: MESSAGE_KEYWORD.VERSION,
    });

  const { request: requestNotification, response: responseNotification } =
    useHandleApp<DeviceTokenData>({
      type: MESSAGE_KEYWORD.NOTIFICATION,
    });

  useEffect(() => {
    if (!osType) return;

    requestAppVersion();
  }, [requestAppVersion, osType]);

  const [isFirstLoading, setIsFirstLoading] = useState<'Y' | 'N'>('Y');

  useEffect(() => {
    if (responseAppVersion?.isSuccess && responseAppVersion?.data) {
      const { version, isFirstLoading } = responseAppVersion.data;
      setAppVersion(version);
      setIsFirstLoading(isFirstLoading);
      //TODO : 버전 체크는 3.1.10 이하의 버전이 안정화되면 제거
      if (osType === 'ios' && version && version >= '3.1.10') {
        document.documentElement.style.setProperty(
          '--safe-area-bottom',
          `${SAFE_AREA_BOTTOM_IOS}px`,
        );

        return () => {
          document.documentElement.style.removeProperty('--safe-area-bottom');
        };
      }
    }
  }, [responseAppVersion, setAppVersion, setIsFirstLoading, osType]);

  useEffect(() => {
    if (!osType) return;

    if (!deviceTokenData && isFirstLoading === 'N') {
      requestNotification();
    }
  }, [deviceTokenData, requestNotification, isFirstLoading, osType]);

  const isLogin = useAuthStore((state) => state.isLogin);

  const { toastError } = useToast();

  const { mutate: saveDeviceTokenMutate } = useMutation({
    mutationFn: (requestBody: DeviceTokenData) =>
      isLogin
        ? AuthApi['post:/wello/v2/auth/wello-device-info']({
            body: {
              ...requestBody,
            },
          })
        : UnauthApi['post:/wello/v2/allows/wello-device-info']({
            body: {
              ...requestBody,
            },
          }),
    onSuccess: () => {
      if (tempDeviceTokenData) {
        setDeviceTokenData(tempDeviceTokenData);
      }
    },
    onError: (error) =>
      toastError({
        error,
      }),
  });

  useEffect(() => {
    if (!responseNotification || isLogin === undefined) return;

    const { isSuccess, data } = responseNotification;

    if (!isSuccess || !data) return;

    setTempDeviceTokenData(data);
  }, [
    isLogin,
    responseNotification,
    saveDeviceTokenMutate,
    setTempDeviceTokenData,
  ]);

  useStatusBarColor({
    color: STATUS_COLOR.DEFAULT,
  });

  useEffect(() => {
    if (tempDeviceTokenData) {
      saveDeviceTokenMutate(tempDeviceTokenData);
    }
  }, [saveDeviceTokenMutate, tempDeviceTokenData]);
};
